import { useEffect, useState } from "react";
import { toast } from "sonner";
import { CalendarCheck2, Check, X, RefreshCw, Sparkles, Loader2, Settings2 } from "lucide-react";
import { api, errMsg } from "../lib/api";
import { Card, Field, Btn, Toggle, inputCls } from "./ui";

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const fmt = (iso) => `${new Date(iso).toLocaleString("en-IN", { timeZone: "Asia/Kolkata", weekday: "short", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })} IST`;

const ConfigForm = ({ cfg, onSaved }) => {
  const [c, setC] = useState(cfg);
  const toggleDay = (d) => setC({ ...c, days: c.days.includes(d) ? c.days.filter((x) => x !== d) : [...c.days, d].sort() });
  const save = async () => {
    try { const { data } = await api.put("/admin/auto-ideas/config", c); toast.success("Weekly ideas settings saved"); onSaved(data); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="mt-4 space-y-4 rounded-xl border border-white/5 bg-black/30 p-4" data-testid="auto-ideas-config">
      <Toggle checked={c.enabled} onChange={(v) => setC({ ...c, enabled: v })} testid="auto-ideas-enabled" label="Create suggestions automatically every Monday" />
      <div>
        <p className="mb-2 text-xs uppercase tracking-wider text-zinc-400">Posting days</p>
        <div className="flex flex-wrap gap-1.5">
          {DAYS.map((d, i) => (
            <button key={d} type="button" onClick={() => toggleDay(i)} data-testid={`auto-ideas-day-${i}`}
              className={`rounded-full px-3 py-1.5 text-xs font-semibold ${c.days.includes(i) ? "bg-[#D4AF37] text-black" : "border border-[#D4AF37]/30 text-[#D4AF37]"}`}>{d}</button>
          ))}
        </div>
      </div>
      <div className="grid gap-3 sm:grid-cols-3">
        <Field label="Publish time (IST)"><input type="time" value={c.time} onChange={(e) => setC({ ...c, time: e.target.value })} className={`${inputCls} [color-scheme:dark]`} data-testid="auto-ideas-time" /></Field>
        <Field label="Focus">
          <select value={c.focus} onChange={(e) => setC({ ...c, focus: e.target.value })} className={inputCls} data-testid="auto-ideas-focus">
            <option value="mixed">Cricket + Casino</option><option value="cricket">Cricket</option><option value="casino">Casino</option>
          </select>
        </Field>
        <Field label="Language">
          <select value={c.language} onChange={(e) => setC({ ...c, language: e.target.value })} className={inputCls} data-testid="auto-ideas-language"><option value="en">English</option><option value="hi">Hindi</option></select>
        </Field>
        <Field label="Words"><input type="number" min={300} max={2500} value={c.words} onChange={(e) => setC({ ...c, words: Number(e.target.value) })} className={inputCls} data-testid="auto-ideas-words" /></Field>
        <Field label="Images inside article">
          <select value={c.inline_images} onChange={(e) => setC({ ...c, inline_images: Number(e.target.value) })} className={inputCls} data-testid="auto-ideas-inline"><option value={0}>None</option><option value={1}>1</option><option value={2}>2</option></select>
        </Field>
        <div className="flex items-end pb-2"><Toggle checked={c.featured_image} onChange={(v) => setC({ ...c, featured_image: v })} testid="auto-ideas-featured" label="Featured image" /></div>
      </div>
      <Field label="Extra direction (optional)"><input value={c.hint} maxLength={200} onChange={(e) => setC({ ...c, hint: e.target.value })} className={inputCls} placeholder="e.g. focus on IPL auction news" data-testid="auto-ideas-hint" /></Field>
      <Btn onClick={save} data-testid="auto-ideas-save">Save settings</Btn>
    </div>
  );
};

const IdeaRow = ({ idea, i, onAct, busy }) => (
  <li className={`flex flex-col gap-2 rounded-xl border p-3 sm:flex-row sm:items-center ${idea.status === "approved" ? "border-emerald-500/30 bg-emerald-500/5" : idea.status === "dismissed" ? "border-white/5 opacity-40" : "border-white/5 bg-black/30"}`} data-testid={`weekly-idea-${i}`}>
    <div className="shrink-0 text-xs font-semibold text-[#F3D77A] sm:w-44">{fmt(idea.scheduled_at)}</div>
    <div className="min-w-0 flex-1">
      <p className={`text-sm text-white ${idea.status === "dismissed" ? "line-through" : ""}`}>{idea.topic}</p>
      <p className="mt-0.5 text-xs text-zinc-500"><span className="mr-2 uppercase tracking-wider text-[10px] text-[#D4AF37]">{idea.category}</span>{idea.why}</p>
    </div>
    {idea.status === "proposed" ? (
      <div className="flex shrink-0 gap-1">
        <button onClick={() => onAct("approve", i)} disabled={busy} className="rounded-full bg-[#D4AF37] p-2 text-black disabled:opacity-50" aria-label="Approve" data-testid={`weekly-idea-approve-${i}`}><Check size={14} /></button>
        <button onClick={() => onAct("swap", i)} disabled={busy} className="rounded-full border border-[#D4AF37]/40 p-2 text-[#D4AF37] disabled:opacity-50" aria-label="Swap idea" data-testid={`weekly-idea-swap-${i}`}><RefreshCw size={14} /></button>
        <button onClick={() => onAct("dismiss", i)} disabled={busy} className="rounded-full border border-white/10 p-2 text-zinc-400 hover:text-red-400 disabled:opacity-50" aria-label="Dismiss" data-testid={`weekly-idea-dismiss-${i}`}><X size={14} /></button>
      </div>
    ) : <span className={`text-[10px] font-semibold uppercase tracking-widest ${idea.status === "approved" ? "text-emerald-400" : "text-zinc-500"}`} data-testid={`weekly-idea-status-${i}`}>{idea.status === "approved" ? "Queued" : "Dismissed"}</span>}
  </li>
);

export const WeeklyIdeas = ({ onQueued }) => {
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);
  const [showCfg, setShowCfg] = useState(false);
  const load = () => api.get("/admin/auto-ideas").then(({ data }) => setD(data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); }, []);

  const run = async (fn, msg) => {
    setBusy(true);
    try { await fn(); if (msg) toast.success(msg); await load(); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const generate = () => run(() => api.post("/admin/auto-ideas/generate", {}, { timeout: 90000 }), "Fresh ideas ready for next week");
  const act = (kind, i) => {
    const id = d.batch.id;
    if (kind === "approve") return run(async () => {
      const { data } = await api.post(`/admin/auto-ideas/${id}/approve`, { indexes: [i] });
      if (data.skipped) throw new Error("That day already has a post — dismiss or swap this idea instead");
      onQueued();
    }, "Added to the queue");
    if (kind === "swap") return run(() => api.post(`/admin/auto-ideas/${id}/swap/${i}`, {}, { timeout: 90000 }), "Swapped for a new idea");
    return run(() => api.post(`/admin/auto-ideas/${id}/dismiss/${i}`));
  };
  const approveAll = () => run(async () => {
    const { data } = await api.post(`/admin/auto-ideas/${d.batch.id}/approve`, {});
    toast.success(`${data.approved} post${data.approved === 1 ? "" : "s"} queued for the week${data.skipped ? ` · ${data.skipped} skipped (day already has a post)` : ""}`);
    onQueued();
  });

  if (!d) return null;
  const pending = d.batch?.ideas.filter((x) => x.status === "proposed").length || 0;
  const weekLabel = d.batch ? new Date(`${d.batch.week_start}T00:00:00`).toLocaleDateString([], { day: "numeric", month: "long" }) : "";
  return (
    <Card title="Weekly auto-ideas" className="mb-6" testid="weekly-ideas">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <p className="max-w-2xl text-sm text-zinc-400">
          Every Monday the AI fills next week's posting slots ({d.config.days.map((x) => DAYS[x]).join(", ")} at {d.config.time} IST) with fresh trending topics.
          {d.config.enabled ? " Approve them here with one tap." : <span className="text-amber-300"> Automatic Monday ideas are off.</span>}
        </p>
        <div className="flex flex-wrap gap-2">
          <Btn variant="outline" onClick={() => setShowCfg((s) => !s)} data-testid="auto-ideas-settings-toggle"><Settings2 size={14} /> Settings</Btn>
          {d.batch && <Btn variant="outline" onClick={() => { if (window.confirm("Replace next week's pending suggestions with new ones? Approved ones stay queued.")) generate(); }} disabled={busy} data-testid="auto-ideas-generate">{busy ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />} Generate now</Btn>}
          {!d.batch && <Btn variant="outline" onClick={generate} disabled={busy} data-testid="auto-ideas-generate">{busy ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />} Generate now</Btn>}
        </div>
      </div>
      {showCfg && <ConfigForm cfg={d.config} onSaved={(c) => { setD({ ...d, config: c }); setShowCfg(false); }} />}
      {d.batch ? (
        <div className="mt-5">
          <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
            <p className="text-xs uppercase tracking-widest text-zinc-500" data-testid="weekly-ideas-week">Week of {weekLabel} · {pending} waiting for approval</p>
            {pending > 0 && <Btn onClick={approveAll} disabled={busy} data-testid="weekly-ideas-approve-all"><CalendarCheck2 size={15} /> Approve all {pending}</Btn>}
          </div>
          <ul className="space-y-2">{d.batch.ideas.map((idea, i) => <IdeaRow key={i} idea={idea} i={i} onAct={act} busy={busy} />)}</ul>
        </div>
      ) : <p className="mt-4 text-sm text-zinc-600" data-testid="weekly-ideas-empty">No suggestions yet — they'll appear next Monday, or click Generate now.</p>}
    </Card>
  );
};
